/**
 * The URL-scrub chokepoint, shared by EVERY analytics vendor.
 *
 * This lived inside `lib/gaPixel.ts` until THE-765 (PR 3.4), which is how the
 * Meta path came to ship the raw `window.location.href`: the policy was only
 * reachable by importing the GA module, so `lib/metaCapi.ts` did not, and
 * `event_source_url` went to Meta's Graph API unscrubbed — resume tokens, OTP
 * emails and holiday deep-link ids included. The list is not a GA concern; it
 * is the answer to "what may never leave the browser", so it is its own module
 * and both vendors spend the same symbol.
 *
 * `lib/gaPixel.ts` re-exports `scrubSensitiveParams` (and the deprecated
 * `scrubResumeToken` alias) so existing importers keep working unchanged.
 *
 * URL query params that must NEVER reach an analytics property:
 *  - `rt`        — session-resume token; a bearer credential (LIVE in prod
 *                  today: a board URL opened in an in-app browser carries it).
 *                  Possession restores the anon session, so it can't land in
 *                  analytics.
 *  - `otp_email` — the email address in the email-OTP sign-in flow (PII).
 *  - `otp_step`  — the OTP flow step; scrubbed alongside otp_email so no OTP
 *                  URL state leaks either.
 *  - `carry`     — the guide "carry this product into the quiz" id. NOT PII (a
 *                  public catalog id), but stripped so a distinct product id
 *                  per guide-save quiz-start doesn't fan `/quiz` out into a
 *                  high-cardinality page_location across GA4 reports.
 *  - `g`         — the v4 claim token (#152); a BEARER CREDENTIAL for the
 *                  guest→account attach grant. Possession lets the holder view
 *                  and claim a board, so it must never reach analytics — same
 *                  class as `rt`. The claim route also strips it from the URL
 *                  via replaceState, but this is the defence that survives if
 *                  the pageview fires before that runs.
 *  - `v4`        — the v4 preview flag. Harmless, but stripped so a flagged
 *                  session doesn't fan every page_location into a `?v4=1` twin.
 */
// `for` carries a recipient's Firestore document id into the quiz (THE-705 /
// WS4 — "Get gift ideas" on a no-quiz person's board attaches the quiz to HER
// instead of minting a second person). An internal document id keyed to one
// user's private subtree has no business in an analytics property, so it is
// scrubbed like the resume token.
//
// `e` is the calendar deep link's exact-event id (`?e=`, THE-746 PR 2.5,
// `calendar/calendarDeepLink.ts`). It is scrubbed for BOTH of its two shapes:
//
//  - `holiday_<eventType>_<year>` — a specific HOLIDAY NAME. THE-746's ground
//    rule is that holiday choices never leave our database, and once Phase 3
//    filters the strip by the user's hidden list, the holidays she can tap are
//    derived from her choices, making this a religion-adjacent signal. The
//    board's rev-4 correction allows the generic `event_type: 'holiday'` and
//    nothing narrower, so the name must not arrive by the back door either.
//    THIS IS NOT A THEORETICAL PATH: `withScrubbedLocation` stamps
//    `page_location` on EVERY hit, so a user who taps a Coming Up holiday card
//    and then taps anything on the calendar sends `calendar_event_tap` with
//    `holiday_hanukkah_2026` in the URL — the generic `event_type` on that
//    same hit would be beside the point.
//  - `<recipientId>_<birthday|anniversary>_<year>` — a recipient's Firestore
//    document id out of one user's private subtree, i.e. exactly the class
//    `for` is scrubbed for. That shape ships today (PR 2.5, THE-758).
//
// Scrubbed rather than renamed or hashed: the id has to collide with the one
// the event rail renders, so it cannot be changed at the writer. Stripping it
// at the single analytics chokepoint is the defence that survives any future
// id scheme — and it costs nothing in reports, because `?e=` is a scroll
// target, never a distinct page.
//
// `e` and `d` now have a writer-side half too, exactly as `g` does:
// `calendar/calendarDeepLink.stripCalendarDeepLinkParams` erases them from the
// address bar once EventList's deep-link scroll resolves (THE-784). That half
// exists because this list cannot reach Meta's automatic `dl` — `fbq()` reads
// `document.location.href` inside third-party code. The two are not redundant:
// the strip is what closes `dl`, and this list is what survives if the strip
// has not run yet (an unresolved link, a sandboxed embed where `replaceState`
// throws, or any hit fired before the calendar mounts).
//
// `d` is the calendar deep link's DAY key (`?d=YYYY-MM-DD`, same module). It is
// scrubbed by THE-765 / PR 3.4, and the reason is the whole point of this
// ticket: **a holiday's date names the holiday**. PR 2.4 scrubbed `e` and
// deliberately kept `d` — its own test asserted `d=2026-12-25` survived — but
// `/calendar?m=2026-12&d=2026-12-25` identifies Christmas exactly as well as
// `e=holiday_christmas_2026` does, so scrubbing one and keeping the other left
// the door open for all 19 fixed-date entries in the 26-holiday `HOLIDAYS`
// table. It is the same arithmetic the `days_until` omission in
// `GaCalendarEventTapParams` already guards against, arriving by a different
// param.
//
// `m` (`?m=YYYY-MM`) deliberately SURVIVES. A month is the page's actual
// content — which month of the calendar she is looking at — and it names no
// holiday on its own: December alone covers Christmas, Hanukkah, Kwanzaa and
// New Year's. The board's rev-4 line is that the generic signal is allowed and
// the specific identifier is not; a month is on the allowed side of it, and
// dropping it would cost the one genuinely useful dimension on these hits.
//
// Person deep links lose their `d` too, which is a deliberate and cheap
// acceptance: `?d=` is a scroll target, never a distinct page (identical to the
// `?e=` rationale above), so no report loses a row — and a recipient's birthday
// date is PII we have no reason to hand to a vendor either, even though
// THE-746's ground rule is specifically about holidays.
// `becomeToken` is the admin-impersonation custom token (scripts/become_user.py
// in thea-serverless → theaWeb/auth/becomeUser.ts): a BEARER CREDENTIAL whose
// possession signs the holder in AS the target user — same class as `rt`/`g`.
// The boot handler strips it via replaceState; this is the defence that
// survives any hit fired before that runs.
const SENSITIVE_URL_PARAMS = [
  'rt',
  'otp_email',
  'otp_step',
  'carry',
  'for',
  'g',
  'v4',
  'e',
  'd',
  'becomeToken',
] as const;

/**
 * Strip every sensitive query param (see SENSITIVE_URL_PARAMS) from a full URL
 * or a `path?search` string, preserving the path, all other params, and the
 * hash. Defensive: returns the input unchanged if it can't be parsed — the
 * analytics path must never throw.
 */
export function scrubSensitiveParams(value: string): string {
  try {
    if (/^https?:\/\//.test(value)) {
      const url = new URL(value);
      SENSITIVE_URL_PARAMS.forEach((p) => url.searchParams.delete(p));
      return url.toString();
    }
    const url = new URL(value, 'https://placeholder.invalid');
    SENSITIVE_URL_PARAMS.forEach((p) => url.searchParams.delete(p));
    return url.pathname + url.search + url.hash;
  } catch {
    return value;
  }
}
